import React, { createRef, lazy, Suspense, useEffect } from 'react';
import { animated } from 'react-spring';
import { Redirect, Route, Switch } from 'react-router-dom';
import { observer } from 'mobx-react';

import Spinner from './components/spinner/Spinner';

import { computeScale } from './helpers/device';

import useAnimate, { AnimateState } from './hooks/useAnimate';
import useResize from './hooks/useResize';

import { useLocalStore, useStore } from './stores';
import UserStore from './stores/UserStore';

import styles from './App.module.scss';

const AuthPage = lazy(() => import('./pages/auth/AuthPage'));
const MainPage = lazy(() => import('./pages/main/MainPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const TermsPage = lazy(() => import('./pages/terms/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/not-found/NotFoundPage'));
export default observer(function App() {
    const viewportRef = createRef<HTMLDivElement>();

    const animate = useAnimate();

    const { authStore } = useStore();
    const { localStore } = useLocalStore({
        isProcess: true,
    });

    // Authenticate user on mount, then load full user object (needed for role check)
    useEffect(() => {
        localStore.isProcess = true;

        authStore.authenticate()
            .then(() => {
                if (authStore.isAuthenticated) {
                    return UserStore.findById(authStore.id).then((user) => {
                        authStore.user = user;
                    }).catch(() => {});
                }
            })
            .finally(() => {
                localStore.isProcess = false;
            });
    }, []);

    // Scale viewport on resize
    useResize(() => {
        if (!viewportRef.current) {
            return;
        }

        const scale = computeScale();

        viewportRef.current.style.height = `${+(100 / scale).toFixed(4)}%`;
        viewportRef.current.style.transform = `scale(${+scale.toFixed(4)})`;

        animate.setScale(scale);
        animate.setAlpha(0, true);
    }, [viewportRef]);

    return (
        <animated.div
            className={styles.viewport}
            style={{
                pointerEvents: animate.to([
                    {
                        states: [AnimateState.Default],
                        update: alpha => (alpha > 0 && alpha < 1 ? 'none' : 'auto'),
                    },
                ]),
                willChange: animate.to([
                    {
                        states: [AnimateState.Default],
                        update: alpha => (alpha > 0 && alpha < 1 ? 'transform, opacity' : 'auto'),
                    },
                ]),
            }}
            ref={viewportRef}
        >
            {localStore.isProcess
                ? (
                    <Spinner/>
                )
                : (
                    <div className={styles.container}>
                        <Suspense fallback={<Spinner/>}>
                            <Switch>
                                <Route path="/auth"><AuthPage/></Route>
                                {authStore.isAuthenticated && <Route path="/search"><MainPage/></Route>}
                                {authStore.isAuthenticated && <Route path="/users/:id"><MainPage/></Route>}
                                {authStore.isAuthenticated && <Route path="/courts/:id"><MainPage/></Route>}
                                {authStore.isAuthenticated && <Route path="/profile"><ProfilePage/></Route>}
                                <Route path="/terms"><TermsPage/></Route>
                                <Route exact path="/">
                                    <Redirect to={authStore.isAuthenticated ? (authStore.user?.role === '' ? '/profile/role' : '/search') : '/auth'}/>
                                </Route>
                                {authStore.isAuthenticated && <Route path="*"><Redirect to={authStore.user?.role === '' ? '/profile/role' : '/search'}/></Route>}
                                {!authStore.isAuthenticated && <Route path="*"><NotFoundPage/></Route>}
                            </Switch>
                        </Suspense>
                    </div>
                )}
        </animated.div>
    );
});
